/* UniBuy™ Customer On-Boarding — Infosys Admin only.
   Route: /infoshop/admin/onboarding (gated to the Infy2026BPM admin login).

   This is a high-fidelity reference screen meant to be Figma-ised by the
   UI/UX team. Keep the IA (information architecture) intact when you
   redesign — the backend workflow depends on the field names.
*/
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Flag from "../../components/Flag";
import {
  Building2, Calendar, FileText, Mail, User2, Globe2, ShieldCheck,
  Search, Copy, Save, Send, RefreshCcw, KeyRound, ListChecks, History,
  CheckCircle2, XCircle, AlertCircle, ArrowRight, Loader2, Sparkles, Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = `${API}/infoshop/admin/onboarding`;

// ─── Small atoms ────────────────────────────────────────────────────────────
const Eyebrow = ({ children, color = "slate" }) => (
  <div className={`text-[10px] uppercase tracking-[0.22em] font-bold text-${color}-500 mb-1.5`}>{children}</div>
);

const Field = ({ label, required, children, hint }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1.5">
      {label}{required && <span className="text-rose-600"> *</span>}
    </div>
    {children}
    {hint && <div className="mt-1 text-[10px] text-slate-400 leading-relaxed">{hint}</div>}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0072CE]/40 focus:border-[#0072CE] disabled:opacity-50 disabled:bg-slate-50"
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0072CE]/40 focus:border-[#0072CE] disabled:opacity-50 disabled:bg-slate-50"
  />
);

const Pill = ({ children, tone = "slate" }) => {
  const tones = {
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
    blue:    "bg-[#0072CE]/10 text-[#1F4E7D] border-[#0072CE]/30",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    green:   "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
    indigo:  "bg-indigo-50 text-indigo-800 border-indigo-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold border ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Card = ({ title, eyebrow, icon: Icon, children, action }) => (
  <section className="rounded-xl bg-white border border-slate-200 shadow-sm">
    <header className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
      {Icon && (
        <div className="h-9 w-9 rounded-lg bg-[#0072CE]/10 text-[#1F4E7D] flex items-center justify-center shrink-0">
          <Icon size={16} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {eyebrow && <Eyebrow color="slate">{eyebrow}</Eyebrow>}
        <h2 className="font-display text-base font-semibold tracking-tight text-slate-900">{title}</h2>
      </div>
      {action}
    </header>
    <div className="p-5">{children}</div>
  </section>
);

const STATE_TONE = {
  DRAFT: "slate",
  PENDING_SALES_VP: "amber",
  PENDING_RAG_FINANCE: "amber",
  PENDING_AGGREGATOR_LEAD: "amber",
  APPROVED_AND_ISSUED: "green",
  REJECTED: "rose",
};

const STATE_LABEL = {
  DRAFT: "Draft",
  PENDING_SALES_VP: "Pending Sales VP",
  PENDING_RAG_FINANCE: "Pending RAG · Finance",
  PENDING_AGGREGATOR_LEAD: "Pending Aggregator Lead",
  APPROVED_AND_ISSUED: "Approved · Credentials Issued",
  REJECTED: "Rejected",
};

// ─── Default form (resets when "New" is clicked) ────────────────────────────
const blankForm = () => ({
  customer_legal_name: "",
  contract_id: "",
  contract_start_date: "",
  contract_end_date: "",
  customer_contact_name: "",
  customer_contact_email: "",
  infosys_sales_contact_name: "",
  infosys_sales_email: "",
  infosys_account_manager_name: "",
  infosys_account_manager_email: "",
  sales_vp_name: "",
  sales_vp_email: "",
  rag_finance_email: "",
  // each country: { iso2, expected_annual_spend, currency, country_discount_pct, excluded_categories: [] }
  countries: [],
  notes: "",
});

// ─── Page ───────────────────────────────────────────────────────────────────
export default function OnboardingPage() {
  const { user } = useAuth();
  const [constants, setConstants] = useState(null);
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [meta, setMeta] = useState(null);          // server-side fields when editing existing
  const [busy, setBusy] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [rejectModal, setRejectModal] = useState(null); // {stage, token}
  const [approveModal, setApproveModal] = useState(null); // {stage, token}

  const isAdmin = user?.username === "Infy2026BPM" || user?.role === "admin";

  // Load constants + list
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const [c, l] = await Promise.all([
          axios.get(`${BASE}/constants`),
          axios.get(BASE),
        ]);
        setConstants(c.data);
        setList(l.data.items || []);
      } catch (err) {
        console.error("[Onboarding] failed to load metadata:", err);
        toast.error("Failed to load on-boarding metadata");
      }
    })();
    // `BASE` / `axios` are module-level constants.
  }, [isAdmin]);

  const categoryByKey = useMemo(() => {
    const o = {};
    (constants?.categories || []).forEach((c) => (o[c.key] = c));
    return o;
  }, [constants]);

  const refreshList = async () => {
    try {
      const l = await axios.get(BASE);
      setList(l.data.items || []);
    } catch (err) { console.warn("[Onboarding] refresh list failed:", err); }
  };

  // ─── Form helpers ─────────────────────────────────────────────────────────
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => {
    setActiveId(null);
    setMeta(null);
    setForm(blankForm());
    setCountrySearch("");
  };

  const loadRecord = async (id) => {
    try {
      const { data } = await axios.get(`${BASE}/${id}`);
      setActiveId(id);
      setMeta(data);
      // Hydrate the form from server fields (drop computed ones)
      const f = blankForm();
      Object.keys(f).forEach((k) => { if (data[k] !== undefined) f[k] = data[k]; });
      setForm(f);
    } catch {
      toast.error("Could not load record");
    }
  };

  const toggleCountry = (iso2) => {
    setForm((f) => {
      const exists = f.countries.find((c) => c.iso2 === iso2);
      if (exists) return { ...f, countries: f.countries.filter((c) => c.iso2 !== iso2) };
      const meta = constants.countries_top50.find((c) => c.iso2 === iso2);
      return {
        ...f,
        countries: [...f.countries, {
          iso2,
          currency: meta?.currency,
          expected_annual_spend: 0,
          country_discount_pct: 0,
          excluded_categories: [],   // default = ALL categories enabled
        }],
      };
    });
  };

  const updateCountry = (iso2, patch) =>
    setForm((f) => ({ ...f, countries: f.countries.map((c) => c.iso2 === iso2 ? { ...c, ...patch } : c) }));

  // Toggle a single leaf category — uncheck = add to excluded list.
  const toggleExcluded = (iso2, categoryKey) => {
    setForm((f) => ({
      ...f,
      countries: f.countries.map((c) => {
        if (c.iso2 !== iso2) return c;
        const isExcluded = c.excluded_categories.includes(categoryKey);
        return {
          ...c,
          excluded_categories: isExcluded
            ? c.excluded_categories.filter((k) => k !== categoryKey)
            : [...c.excluded_categories, categoryKey],
        };
      }),
    }));
  };

  // Toggle an entire super-group (parent checkbox). If any child is
  // currently included, the parent click *excludes the whole group*. If
  // everything in the group is already excluded, the parent click
  // re-enables the whole group.
  const toggleGroup = (iso2, groupKey) => {
    setForm((f) => ({
      ...f,
      countries: f.countries.map((c) => {
        if (c.iso2 !== iso2) return c;
        const childKeys = (constants?.categories || []).filter((cat) => cat.group === groupKey).map((cat) => cat.key);
        const allExcluded = childKeys.every((k) => c.excluded_categories.includes(k));
        if (allExcluded) {
          // re-enable all children
          return { ...c, excluded_categories: c.excluded_categories.filter((k) => !childKeys.includes(k)) };
        }
        // exclude all children
        const next = new Set(c.excluded_categories);
        childKeys.forEach((k) => next.add(k));
        return { ...c, excluded_categories: Array.from(next) };
      }),
    }));
  };

  const copyCountryAcrossAll = (fromIso) =>
    setForm((f) => {
      const src = f.countries.find((c) => c.iso2 === fromIso);
      if (!src) return f;
      return {
        ...f,
        countries: f.countries.map((c) => c.iso2 === fromIso ? c : {
          ...c,
          country_discount_pct: src.country_discount_pct,
          excluded_categories: [...src.excluded_categories],
        }),
      };
    });

  // "Apply XX% to all countries" — master input at the top of section 4.
  const applyDiscountToAllCountries = (pct) => {
    const safe = Math.max(0, Math.min(100, Number(pct) || 0));
    setForm((f) => ({
      ...f,
      countries: f.countries.map((c) => ({ ...c, country_discount_pct: safe })),
    }));
  };

  // ─── Computed blended discount (live, frontend mirror of backend math) ────
  // v3.3 — discount is country-level only; the badge per country is just
  // the slider/input value; overall is spend-weighted across countries
  // (falls back to plain mean when no spend data on any country).
  const overallBlended = useMemo(() => {
    if (!form.countries.length) return 0;
    let num = 0;
    let den = 0;
    form.countries.forEach((c) => {
      const pct = Number(c?.country_discount_pct || 0);
      const w = Number(c.expected_annual_spend || 0);
      num += pct * w;
      den += w;
    });
    if (den > 0) return Math.round((num / den) * 100) / 100;
    const arr = form.countries.map((c) => Number(c?.country_discount_pct || 0));
    return Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 100) / 100;
  }, [form.countries]);

  // ─── Mutations ────────────────────────────────────────────────────────────
  // Returns the resulting record so callers can chain (e.g. Submit auto-
  // saves first and then transitions the freshly-created draft).
  const saveDraft = async ({ silent } = {}) => {
    setBusy(true);
    try {
      let data;
      if (activeId) {
        ({ data } = await axios.put(`${BASE}/${activeId}`, form));
        setMeta(data);
        if (!silent) toast.success("Draft updated");
      } else {
        ({ data } = await axios.post(BASE, form));
        setActiveId(data.id);
        setMeta(data);
        if (!silent) toast.success(`Draft saved · ${data.id}`);
      }
      await refreshList();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setBusy(false);
    }
  };

  // Auto-saves the form first if no draft has been created yet — the
  // user no longer needs to remember to click "Save draft" before
  // "Submit for approval". A toast tells them what happened.
  const submitForApproval = async () => {
    setBusy(true);
    try {
      let id = activeId;
      if (!id) {
        toast.message("Saving your draft first…");
        const created = await saveDraft({ silent: true });
        if (!created?.id) return;       // saveDraft already toasted the error
        id = created.id;
      }
      const { data } = await axios.post(`${BASE}/${id}/submit`);
      setMeta(data);
      toast.success("Submitted — Sales VP notified");
      await refreshList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  const approveAtCurrentStage = async ({ confirmed, file, comment }) => {
    if (!meta || !meta.magic_tokens) return;
    const stage = stageForState(meta.state);
    const token = stage && meta.magic_tokens[stage];
    if (!token) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("magic_token", token);
      fd.append("actor_name", "Magic-link demo approver");
      if (comment) fd.append("comment", comment);
      if (confirmed) fd.append("confirmed_out_of_band", "true");
      if (file) fd.append("evidence_file", file);
      const { data } = await axios.post(`${BASE}/${activeId}/approve`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMeta(data);
      toast.success("Stage approved");
      await refreshList();
      setApproveModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const rejectAtCurrentStage = async ({ reason, file }) => {
    if (!meta || !meta.magic_tokens) return;
    const stage = stageForState(meta.state);
    const token = stage && meta.magic_tokens[stage];
    if (!token) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("magic_token", token);
      fd.append("reason", reason);
      if (file) fd.append("evidence_file", file);
      const { data } = await axios.post(`${BASE}/${activeId}/reject`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMeta(data);
      toast.error(`Rejected at ${stage} — record back to DRAFT for revision`);
      await refreshList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
      setRejectModal(null);
    }
  };

  const reopenRecord = async () => {
    if (!activeId) return;
    if (!window.confirm("Reopen this approved record for edit?\n\nThe state will reset to DRAFT and the record must be re-routed through Sales VP → RAG Finance → Aggregator Lead. Previously-issued credentials remain valid.")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/${activeId}/reopen`);
      setMeta(data);
      toast.success("Record reopened — now editable. Re-submit to route through approvals again.");
      await refreshList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reopen failed");
    } finally {
      setBusy(false);
    }
  };

  // ─── Filters ──────────────────────────────────────────────────────────────
  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    if (!constants?.countries_top50) return [];
    if (!q) return constants.countries_top50;
    return constants.countries_top50.filter((c) =>
      c.name.toLowerCase().includes(q) || c.iso2.toLowerCase().includes(q) || c.region.toLowerCase().includes(q));
  }, [constants, countrySearch]);

  // ─── Guard ────────────────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <Lock className="mx-auto text-slate-400" size={28} />
          <h2 className="mt-4 font-display text-lg font-semibold text-slate-900">Admin-only screen</h2>
          <p className="mt-2 text-sm text-slate-600">
            Customer On-Boarding is restricted to the Infosys BPM admin role. Sign in as the
            Infosys admin user to access this workbench.
          </p>
          <Link to="/" data-testid="back-home-from-onboarding" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0072CE] hover:underline">
            <ArrowRight size={14} className="rotate-180" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  const stage = stageForState(meta?.state);
  const hasMagicToken = stage && meta?.magic_tokens?.[stage];

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="onboarding-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3 flex items-center gap-3">
          <Link to="/infoshop" data-testid="onb-home-link" className="text-xs font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
            <ArrowRight size={12} className="rotate-180" /> Back to UniBuy™
          </Link>
          <div className="h-4 w-px bg-slate-300 mx-1" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#1F4E7D]">Admin · Customer Lifecycle</div>
            <h1 className="font-display text-base lg:text-lg font-semibold tracking-tight text-slate-900">UniBuy™ Customer On-Boarding</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={startNew}
              data-testid="onb-new-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 hover:border-slate-900 text-slate-900 rounded-md px-3 py-2"
            >
              <Sparkles size={12} /> New on-boarding
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: existing records (compact list) */}
        <aside className="lg:col-span-3 space-y-3">
          <div className="rounded-xl bg-white border border-slate-200 shadow-sm">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <ListChecks size={14} className="text-slate-500" />
              <h3 className="font-display text-xs font-semibold text-slate-700">On-boarding Records</h3>
              <span className="ml-auto text-[10px] text-slate-400">{list.length}</span>
            </div>
            <div className="max-h-[480px] overflow-y-auto">
              {list.length === 0 && <div className="px-4 py-8 text-xs text-slate-500 text-center">No records yet</div>}
              {list.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => loadRecord(r.id)}
                  data-testid={`onb-list-${r.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${activeId === r.id ? "bg-[#0072CE]/5" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] text-slate-700 truncate">{r.id}</span>
                    <Pill tone={STATE_TONE[r.state]}>{STATE_LABEL[r.state]}</Pill>
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-900 truncate">{r.customer_legal_name || "Untitled"}</div>
                  <div className="mt-0.5 text-[10px] text-slate-500 truncate">{r.contract_id} · {r.countries?.length || 0} country/ies · blend {r.overall_blended_discount_pct || 0}%</div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* CENTER: the form */}
        <div className="lg:col-span-6 space-y-5">
          {/* State banner */}
          {meta && (
            <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Record</div>
              <span className="font-mono text-xs text-slate-700">{meta.id}</span>
              <span className="h-3 w-px bg-slate-300" />
              <Pill tone={STATE_TONE[meta.state]}>{STATE_LABEL[meta.state]}</Pill>
              <span className="ml-auto text-[10px] text-slate-400">Updated {new Date(meta.updated_at).toLocaleString()}</span>
            </div>
          )}

          {/* Section 1 — Customer identity */}
          <Card title="Customer identity" icon={Building2} eyebrow="Section 1 of 5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Customer name (Legal Entity)"
                required
                hint="Must match the Customer Legal Entity Name as recorded in Infosys SAP ERP."
              >
                <Input
                  value={form.customer_legal_name}
                  onChange={(e) => set("customer_legal_name", e.target.value)}
                  disabled={meta && !["DRAFT","REJECTED"].includes(meta.state)}
                  data-testid="onb-customer-legal-name"
                  placeholder="e.g. Danone Trading B.V."
                />
              </Field>
              <Field label="Contract ID" required hint="Infosys SAP master contract id.">
                <Input
                  value={form.contract_id}
                  onChange={(e) => set("contract_id", e.target.value)}
                  disabled={meta && !["DRAFT","REJECTED"].includes(meta.state)}
                  data-testid="onb-contract-id"
                  placeholder="INF-CTR-2026-0xxx"
                />
              </Field>
              <Field label="Contract start" required>
                <Input
                  type="date"
                  value={form.contract_start_date}
                  onChange={(e) => set("contract_start_date", e.target.value)}
                  disabled={meta && !["DRAFT","REJECTED"].includes(meta.state)}
                  data-testid="onb-contract-start"
                />
              </Field>
              <Field label="Contract end" required>
                <Input
                  type="date"
                  value={form.contract_end_date}
                  onChange={(e) => set("contract_end_date", e.target.value)}
                  disabled={meta && !["DRAFT","REJECTED"].includes(meta.state)}
                  data-testid="onb-contract-end"
                />
              </Field>
            </div>
          </Card>

          {/* Section 2 — Contacts */}
          <Card title="Contacts" icon={User2} eyebrow="Section 2 of 5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
              <Field label="Customer contact name" required>
                <Input value={form.customer_contact_name} onChange={(e) => set("customer_contact_name", e.target.value)} data-testid="onb-cust-name" />
              </Field>
              <Field label="Customer contact email" required>
                <Input type="email" value={form.customer_contact_email} onChange={(e) => set("customer_contact_email", e.target.value)} data-testid="onb-cust-email" placeholder="name@customer.com" />
              </Field>

              <Field label="Infosys sales contact name" required>
                <Input value={form.infosys_sales_contact_name} onChange={(e) => set("infosys_sales_contact_name", e.target.value)} data-testid="onb-sales-name" />
              </Field>
              <Field label="Infosys sales email" required>
                <Input type="email" value={form.infosys_sales_email} onChange={(e) => set("infosys_sales_email", e.target.value)} data-testid="onb-sales-email" placeholder="name@infosys.com" />
              </Field>

              <Field label="Infosys account manager name" required>
                <Input value={form.infosys_account_manager_name} onChange={(e) => set("infosys_account_manager_name", e.target.value)} data-testid="onb-kam-name" />
              </Field>
              <Field label="Infosys account manager email" required>
                <Input type="email" value={form.infosys_account_manager_email} onChange={(e) => set("infosys_account_manager_email", e.target.value)} data-testid="onb-kam-email" placeholder="name@infosys.com" />
              </Field>
            </div>
          </Card>

          {/* Section 3 — Country selection */}
          <Card
            title="Country footprint"
            icon={Globe2}
            eyebrow="Section 3 of 5"
            action={
              <div className="relative w-56">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder="Search aggregator countries…"
                  data-testid="onb-country-search"
                  style={{ paddingLeft: 28 }}
                />
              </div>
            }
          >
            <div className="text-[11px] text-slate-500 mb-3">
              Select every country where UniBuy™ will be enabled for this customer. Source: <strong>Infosys Aggregator Countries (Current and In-Pipeline)</strong>.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {filteredCountries.map((c) => {
                const selected = !!form.countries.find((x) => x.iso2 === c.iso2);
                return (
                  <label
                    key={c.iso2}
                    data-testid={`onb-country-${c.iso2}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm
                      ${selected ? "border-[#0072CE] bg-[#0072CE]/5" : "border-slate-200 hover:bg-slate-50"}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleCountry(c.iso2)}
                      className="h-4 w-4 accent-[#0072CE]"
                    />
                    <span className="shrink-0 inline-flex" data-testid={`onb-country-flag-${c.iso2}`}>
                      <Flag code={c.iso2} height={16} />
                    </span>
                    <span className="flex-1 truncate">
                      <span className="font-semibold text-slate-900">{c.name}</span>
                      <span className="ml-1 text-[10px] text-slate-500 font-mono">{c.iso2}</span>
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">{c.currency}</span>
                  </label>
                );
              })}
            </div>
          </Card>

          {/* Section 4 — Per-country configuration */}
          {form.countries.length > 0 && (
            <Card title="Country configuration" icon={ShieldCheck} eyebrow="Section 4 of 5">
              {/* Bulk apply discount across every selected country */}
              <BulkDiscountBar onApply={applyDiscountToAllCountries} />

              <div className="space-y-4 mt-4">
                {form.countries.map((country) => {
                  const meta_c = constants.countries_top50.find((x) => x.iso2 === country.iso2);
                  const blended = Number(country?.country_discount_pct || 0);
                  return (
                    <div key={country.iso2} data-testid={`onb-cc-${country.iso2}`} className="rounded-lg border border-slate-200 bg-slate-50/40 overflow-hidden">
                      <header className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
                        <span className="shrink-0 inline-flex" data-testid={`onb-cc-flag-${country.iso2}`}>
                          <Flag code={country.iso2} height={20} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-sm font-semibold text-slate-900">{meta_c?.name}</div>
                          <div className="text-[10px] text-slate-500 uppercase tracking-[0.15em] font-bold">{meta_c?.region} · {meta_c?.currency}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Customer discount</span>
                          <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-mono font-bold" data-testid={`onb-blend-${country.iso2}`}>
                            {blended}%
                          </span>
                        </div>
                      </header>

                      <div className="p-4 space-y-5">
                        {/* Spend + per-country discount + bulk copy */}
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                          <div className="md:col-span-4">
                            <Field label={`Expected annual spend (${meta_c?.currency})`}>
                              <Input
                                type="number"
                                min="0"
                                value={country.expected_annual_spend}
                                onChange={(e) => updateCountry(country.iso2, { expected_annual_spend: Number(e.target.value || 0) })}
                                data-testid={`onb-spend-${country.iso2}`}
                                placeholder="0"
                              />
                            </Field>
                          </div>
                          <div className="md:col-span-6">
                            <Field
                              label="Customer discount in this country"
                              hint="Single discount applied to every non-excluded category. Drag the slider or type the exact %."
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  step="1"
                                  value={country.country_discount_pct}
                                  onChange={(e) => updateCountry(country.iso2, { country_discount_pct: Number(e.target.value) })}
                                  data-testid={`onb-discount-slider-${country.iso2}`}
                                  className="flex-1 accent-[#0072CE]"
                                />
                                <div className="relative w-24">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    value={country.country_discount_pct}
                                    onChange={(e) => updateCountry(country.iso2, { country_discount_pct: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                                    data-testid={`onb-discount-input-${country.iso2}`}
                                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 pr-7 text-sm font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0072CE]/40 focus:border-[#0072CE]"
                                  />
                                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">%</span>
                                </div>
                              </div>
                            </Field>
                          </div>
                          <div className="md:col-span-2 flex items-end">
                            <button
                              type="button"
                              onClick={() => copyCountryAcrossAll(country.iso2)}
                              data-testid={`onb-copy-all-${country.iso2}`}
                              className="w-full text-xs font-semibold inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 hover:border-slate-900 text-slate-700"
                              title="Copy this country's discount % and category exclusions to every other selected country"
                            >
                              <Copy size={12} /> Copy to all
                            </button>
                          </div>
                        </div>

                        {/* Categories — 5 normalised super-groups, default ALL included */}
                        <div>
                          <Eyebrow>Categories available to this customer in {meta_c?.name}</Eyebrow>
                          <div className="text-[11px] text-slate-500 mb-2">
                            All Infosys-normalised UNSPSC families are enabled by default. <strong>Uncheck</strong> any category your customer does NOT want their users to be able to shop from in this country.
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {(constants.category_groups || []).map((group) => (
                              <CategoryGroupCard
                                key={group.key}
                                group={group}
                                allCategories={constants.categories}
                                excludedCategories={country.excluded_categories}
                                isoForTestId={country.iso2}
                                onToggleGroup={() => toggleGroup(country.iso2, group.key)}
                                onToggleLeaf={(k) => toggleExcluded(country.iso2, k)}
                              />
                            ))}
                          </div>
                          {country.excluded_categories.length > 0 && (
                            <div className="mt-3 text-[11px] text-rose-700 inline-flex items-center gap-1.5">
                              <XCircle size={11} />
                              {country.excluded_categories.length} categor{country.excluded_categories.length === 1 ? "y" : "ies"} excluded — users from this customer will not see them in {meta_c?.name}.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-2 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-3">
                  <Sparkles size={14} className="text-emerald-700" />
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Overall blended customer discount</div>
                  <span className="text-[10px] text-emerald-700/80">spend-weighted across {form.countries.length} country/ies</span>
                  <span className="ml-auto font-mono text-base font-bold text-emerald-800" data-testid="onb-overall-blend">{overallBlended}%</span>
                </div>
              </div>
            </Card>
          )}

          {/* Section 5 — Approval routing + notes */}
          <Card title="Approval routing" icon={ShieldCheck} eyebrow="Section 5 of 5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Sales VP name" required>
                <Input value={form.sales_vp_name} onChange={(e) => set("sales_vp_name", e.target.value)} data-testid="onb-vp-name" />
              </Field>
              <Field label="Sales VP email" required>
                <Input type="email" value={form.sales_vp_email} onChange={(e) => set("sales_vp_email", e.target.value)} data-testid="onb-vp-email" placeholder="vp@infosys.com" />
              </Field>
              <Field label="RAG · Finance Revenue email" required>
                <Input type="email" value={form.rag_finance_email} onChange={(e) => set("rag_finance_email", e.target.value)} data-testid="onb-rag-email" placeholder="rag.review@infosys.com" />
              </Field>
              <Field label="Aggregator Business Leader (final)">
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {constants?.aggregator_leader?.name} · <span className="text-slate-500">{constants?.aggregator_leader?.email}</span>
                  <div className="text-[10px] text-slate-400 uppercase tracking-[0.18em] font-bold mt-0.5">Hard-wired — final sign-off</div>
                </div>
              </Field>
            </div>
            <div className="mt-5">
              <Field label="Notes (optional)">
                <Textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="onb-notes" placeholder="Anything the approvers should know…" />
              </Field>
            </div>
          </Card>

          {/* Action bar */}
          <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={saveDraft}
              disabled={busy}
              data-testid="onb-save-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 hover:border-slate-900 text-slate-900 rounded-md px-4 py-2 disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {activeId ? "Update draft" : "Save draft"}
            </button>
            <button
              type="button"
              onClick={submitForApproval}
              disabled={busy || (meta && !["DRAFT", "REJECTED"].includes(meta.state))}
              data-testid="onb-submit-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white rounded-md px-4 py-2 disabled:opacity-50"
              style={{ background: "linear-gradient(180deg, #0072CE 0%, #2E6FA8 100%)" }}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Submit for approval
            </button>
            {!activeId && (
              <span className="text-[11px] text-slate-500 leading-tight max-w-[260px]" data-testid="onb-submit-hint">
                Submit will <strong className="text-slate-800">auto-save your draft first</strong>, then route it to the Sales VP.
              </span>
            )}
            {meta && stage && (
              <>
                <span className="h-5 w-px bg-slate-300 mx-1" />
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Magic-link (demo)</span>
                <button
                  type="button"
                  onClick={() => setApproveModal({ stage, token: meta.magic_tokens[stage] })}
                  disabled={busy || !hasMagicToken}
                  data-testid="onb-approve-stage-btn"
                  className="inline-flex items-center gap-2 text-xs font-semibold border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-md px-3 py-2 disabled:opacity-50"
                >
                  <CheckCircle2 size={12} /> Approve current stage
                </button>
                <button
                  type="button"
                  onClick={() => setRejectModal({ stage, token: meta.magic_tokens[stage] })}
                  disabled={busy || !hasMagicToken}
                  data-testid="onb-reject-stage-btn"
                  className="inline-flex items-center gap-2 text-xs font-semibold border border-rose-300 text-rose-800 hover:bg-rose-50 rounded-md px-3 py-2 disabled:opacity-50"
                >
                  <XCircle size={12} /> Reject…
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => activeId && loadRecord(activeId)}
              disabled={busy || !activeId}
              data-testid="onb-refresh-btn"
              className="ml-auto inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-900 px-2 py-2"
              title="Reload from server"
            >
              <RefreshCcw size={12} /> Reload
            </button>
          </div>
        </div>

        {/* RIGHT: trail + credentials */}
        <aside className="lg:col-span-3 space-y-4">
          {meta && (
            <>
              {/* Approval trail */}
              <div className="rounded-xl bg-white border border-slate-200 shadow-sm">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  <History size={14} className="text-slate-500" />
                  <h3 className="font-display text-xs font-semibold text-slate-700">Approval trail</h3>
                </div>
                <ul className="max-h-[400px] overflow-y-auto divide-y divide-slate-100">
                  {(meta.trail || []).slice().reverse().map((evt, i) => <TrailItem key={i} evt={evt} />)}
                  {(meta.trail || []).length === 0 && (
                    <li className="px-4 py-6 text-center text-xs text-slate-500">No events yet</li>
                  )}
                </ul>
              </div>

              {/* Issued credentials + reopen-for-edit */}
              {meta.credentials && (
                <CredentialsCard
                  creds={meta.credentials}
                  canReopen={meta.state === "APPROVED_AND_ISSUED"}
                  onReopen={reopenRecord}
                  busy={busy}
                />
              )}
            </>
          )}
        </aside>
      </div>

      {/* Reject modal */}
      {rejectModal && (
        <RejectModal
          stage={rejectModal.stage}
          onCancel={() => setRejectModal(null)}
          onConfirm={rejectAtCurrentStage}
          busy={busy}
        />
      )}

      {/* Approve modal */}
      {approveModal && (
        <ApproveModal
          stage={approveModal.stage}
          onCancel={() => setApproveModal(null)}
          onConfirm={approveAtCurrentStage}
          busy={busy}
        />
      )}
    </div>
  );
}

// ─── Tiny subcomponents ─────────────────────────────────────────────────────
function BulkDiscountBar({ onApply }) {
  const [pct, setPct] = useState("");
  const apply = () => {
    if (pct === "" || isNaN(Number(pct))) return;
    onApply(pct);
    toast.success(`${pct}% applied to every selected country`);
  };
  return (
    <div className="rounded-lg border-2 border-dashed border-[#0072CE]/30 bg-[#0072CE]/5 p-3 flex flex-wrap items-center gap-3" data-testid="onb-bulk-discount-bar">
      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#1F4E7D]">
        <Sparkles size={12} /> Bulk action
      </div>
      <span className="text-xs text-slate-700">Apply the same customer discount to every selected country:</span>
      <div className="relative">
        <input
          type="number"
          min="0"
          max="100"
          step="0.5"
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
          data-testid="onb-bulk-discount-input"
          placeholder="0"
          className="w-24 rounded-md border border-slate-300 bg-white px-3 py-1.5 pr-6 text-sm font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0072CE]/40 focus:border-[#0072CE]"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">%</span>
      </div>
      <button
        type="button"
        onClick={apply}
        data-testid="onb-bulk-discount-apply"
        className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white"
        style={{ background: "linear-gradient(180deg, #0072CE 0%, #2E6FA8 100%)" }}
      >
        Apply to all countries
      </button>
    </div>
  );
}

function CategoryGroupCard({ group, allCategories, excludedCategories, isoForTestId, onToggleGroup, onToggleLeaf }) {
  const children = allCategories.filter((c) => c.group === group.key);
  const excludedInGroup = children.filter((c) => excludedCategories.includes(c.key)).length;
  const allExcluded = excludedInGroup === children.length && children.length > 0;
  const parentChecked = !allExcluded; // checked = at least one child included
  const partial = excludedInGroup > 0 && !allExcluded;
  const groupSlug = group.key.replace(/[^A-Za-z0-9]+/g, "_").toLowerCase();

  return (
    <div className={`rounded-md border ${allExcluded ? "border-rose-200 bg-rose-50/50" : "border-[#0072CE]/30 bg-white"} p-3`}>
      <div className="flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={parentChecked}
          ref={(el) => { if (el) el.indeterminate = partial; }}
          onChange={onToggleGroup}
          data-testid={`onb-group-${isoForTestId}-${groupSlug}`}
          className="h-4 w-4 mt-0.5 accent-[#0072CE]"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-display font-semibold text-slate-900">{group.key}</div>
          <div className="text-[10px] text-slate-500 leading-relaxed">{group.blurb}</div>
        </div>
        {partial && (
          <span className="shrink-0 text-[9px] uppercase tracking-[0.18em] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
            {excludedInGroup}/{children.length} excl.
          </span>
        )}
      </div>
      <div className="mt-2 pl-7 space-y-1">
        {children.map((c) => {
          const excluded = excludedCategories.includes(c.key);
          return (
            <label
              key={c.key}
              className={`flex items-center gap-2 text-[12px] cursor-pointer ${excluded ? "text-slate-400 line-through" : "text-slate-700"}`}
            >
              <input
                type="checkbox"
                checked={!excluded}
                onChange={() => onToggleLeaf(c.key)}
                data-testid={`onb-cat-${isoForTestId}-${c.unspsc_family}`}
                className="h-3.5 w-3.5 accent-[#0072CE]"
              />
              <span className="flex-1 truncate">{c.key}</span>
              <span className="font-mono text-[10px] text-slate-400">UNSPSC {c.unspsc_family}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function stageForState(state) {
  return {
    PENDING_SALES_VP: "sales_vp",
    PENDING_RAG_FINANCE: "rag_finance",
    PENDING_AGGREGATOR_LEAD: "aggregator_lead",
  }[state] || null;
}

function TrailItem({ evt }) {
  const Icon = ({
    created: Sparkles,
    submitted: Send,
    email_sent: Mail,
    approved: CheckCircle2,
    rejected: XCircle,
    credentials_issued: KeyRound,
    updated: FileText,
    reopened_to_draft: RefreshCcw,
    reopened: RefreshCcw,
  }[evt.kind]) || AlertCircle;
  const tone = {
    approved: "text-emerald-600",
    credentials_issued: "text-emerald-600",
    rejected: "text-rose-600",
    submitted: "text-[#0072CE]",
    email_sent: "text-slate-500",
    reopened: "text-amber-600",
  }[evt.kind] || "text-slate-400";

  return (
    <li className="px-4 py-3 flex items-start gap-3">
      <Icon size={14} className={`${tone} mt-0.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold text-slate-900 capitalize">{evt.kind.replace(/_/g, " ")}</div>
        {evt.stage && <div className="text-[10px] text-slate-500 truncate uppercase tracking-wider">stage · {evt.stage.replace(/_/g, " ")}</div>}
        {evt.to_email && <div className="text-[10px] text-slate-500 truncate">→ {evt.to_email}</div>}
        {evt.subject && <div className="text-[10px] text-slate-500 truncate italic">{evt.subject}</div>}
        {evt.actor_name && <div className="text-[10px] text-slate-500 truncate">by {evt.actor_name}</div>}
        {evt.by && <div className="text-[10px] text-slate-500 truncate">by {evt.by}</div>}
        {evt.reason && <div className="text-[10px] text-rose-700 mt-0.5">{evt.reason}</div>}
        {evt.comment && <div className="text-[10px] text-slate-600 mt-0.5">“{evt.comment}”</div>}
        {evt.note && <div className="text-[10px] text-amber-700 mt-0.5">{evt.note}</div>}
        {evt.confirmed_out_of_band && (
          <div className="text-[10px] text-slate-500 italic mt-0.5">Approver confirmed email approval was received out-of-band.</div>
        )}
        {evt.evidence && (
          <a
            href={`${API}${evt.evidence.url}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`onb-trail-evidence-${evt.kind}-${evt.stage || ""}`}
            className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-[#0072CE] hover:underline truncate max-w-full"
          >
            <FileText size={10} className="shrink-0" />
            <span className="truncate">{evt.evidence.original_filename || evt.evidence.filename}</span>
            <span className="text-slate-400">· {(evt.evidence.size_bytes / 1024).toFixed(0)} KB</span>
          </a>
        )}
        <div className="text-[9px] text-slate-400 mt-0.5 font-mono">{new Date(evt.at).toLocaleString()}</div>
      </div>
    </li>
  );
}

function CredentialsCard({ creds, canReopen, onReopen, busy }) {
  const copy = (v, label) => {
    navigator.clipboard?.writeText(v);
    toast.success(`${label} copied`);
  };
  return (
    <div data-testid="onb-credentials" className="rounded-xl bg-white border-2 border-emerald-300 shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-emerald-600 text-white flex items-center gap-2">
        <KeyRound size={14} />
        <h3 className="font-display text-xs font-semibold tracking-tight">Credentials issued</h3>
      </div>
      <div className="p-4 space-y-3">
        <CredField label="Tenant slug" value={creds.tenant_slug} onCopy={copy} />
        <CredField label="PunchOut URL" value={creds.punchout_url} onCopy={copy} />
        <CredField label="API key" value={creds.api_key} onCopy={copy} secret />
        <CredField label="Webhook secret" value={creds.webhook_secret} onCopy={copy} secret />
        <div className="pt-2 mt-2 border-t border-slate-100 text-[10px] text-slate-500 leading-relaxed">
          Emailed (mocked) to <strong>{(creds.issued_to_emails || []).join(", ")}</strong> on {new Date(creds.issued_at).toLocaleString()}.
          Treat the API key and webhook secret as secrets — rotate via the OMNISupply admin console.
        </div>
        {canReopen && (
          <button
            type="button"
            onClick={onReopen}
            disabled={busy}
            data-testid="onb-reopen-btn"
            className="w-full mt-1 inline-flex items-center justify-center gap-2 text-xs font-semibold border border-amber-300 text-amber-800 hover:bg-amber-50 rounded-md px-3 py-2 disabled:opacity-50"
          >
            <RefreshCcw size={12} /> Reopen for edit · re-route through approvals
          </button>
        )}
      </div>
    </div>
  );
}

function CredField({ label, value, onCopy, secret }) {
  const [show, setShow] = useState(!secret);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-1.5">
        <code className="flex-1 truncate font-mono text-[11px] bg-slate-50 border border-slate-200 px-2 py-1.5 rounded">
          {show ? value : "•".repeat(Math.min(value.length, 18))}
        </code>
        {secret && (
          <button type="button" onClick={() => setShow((s) => !s)} className="text-[10px] text-slate-500 hover:text-slate-900 px-1.5 py-1">
            {show ? "Hide" : "Show"}
          </button>
        )}
        <button type="button" onClick={() => onCopy(value, label)} className="text-[10px] text-slate-500 hover:text-slate-900 px-1.5 py-1 inline-flex items-center gap-1">
          <Copy size={10} /> Copy
        </button>
      </div>
    </div>
  );
}

function RejectModal({ stage, onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4" data-testid="onb-reject-modal">
      <div className="w-full max-w-md rounded-xl bg-white border border-slate-200 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-100">
          <Eyebrow color="rose">Reject · {stage.replace(/_/g, " ")}</Eyebrow>
          <h3 className="font-display text-base font-semibold text-slate-900">Send back for revision</h3>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Reason (required, ≥ 4 chars)" required>
            <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Discount % on Safety (PPE) is above the approved guardrail." data-testid="onb-reject-reason" />
          </Field>
          <Field label="Email-trail evidence (optional)" hint="PDF / EML / MSG / PNG / JPG up to 12 MB. Attach the email from the approver requesting changes so the audit trail captures the source.">
            <input
              type="file"
              accept=".pdf,.eml,.msg,.png,.jpg,.jpeg,.heic,.webp,.txt,application/pdf,image/*,message/rfc822"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="onb-reject-file"
              className="block w-full text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
            {file && <div className="mt-1 text-[10px] text-slate-500 truncate">Selected: {file.name} ({(file.size / 1024).toFixed(0)} KB)</div>}
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
          <button
            type="button"
            disabled={busy || reason.trim().length < 4}
            onClick={() => onConfirm({ reason: reason.trim(), file })}
            data-testid="onb-reject-confirm"
            className="text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-md px-3 py-2 disabled:opacity-50"
          >
            {busy ? "Sending…" : "Reject and send back to DRAFT"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApproveModal({ stage, onCancel, onConfirm, busy }) {
  const [file, setFile] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [comment, setComment] = useState("");
  const ready = !!file || confirmed;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4" data-testid="onb-approve-modal">
      <div className="w-full max-w-md rounded-xl bg-white border border-slate-200 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-100">
          <Eyebrow color="green">Approve · {stage.replace(/_/g, " ")}</Eyebrow>
          <h3 className="font-display text-base font-semibold text-slate-900">Confirm approval for this stage</h3>
          <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
            Approvers must either attach the email approving this stage <strong>or</strong> tick the
            confirmation below to record that approval was received out-of-band.
          </p>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Email-trail evidence" hint="PDF / EML / MSG / PNG / JPG up to 12 MB. Recommended — keeps the audit trail intact.">
            <input
              type="file"
              accept=".pdf,.eml,.msg,.png,.jpg,.jpeg,.heic,.webp,.txt,application/pdf,image/*,message/rfc822"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="onb-approve-file"
              className="block w-full text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-800 hover:file:bg-emerald-100"
            />
            {file && <div className="mt-1 text-[10px] text-slate-500 truncate">Selected: {file.name} ({(file.size / 1024).toFixed(0)} KB)</div>}
          </Field>
          <label className="flex items-start gap-2.5 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="onb-approve-confirm-checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="leading-snug">
              I confirm email approval for this stage was received out-of-band and a copy of the approval has been retained outside UniBuy™.
            </span>
          </label>
          <Field label="Comment (optional)">
            <Textarea
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="onb-approve-comment"
              placeholder="e.g. Approved on the Q1 commercial review call."
            />
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2">Cancel</button>
          <button
            type="button"
            disabled={busy || !ready}
            onClick={() => onConfirm({ file, confirmed, comment: comment.trim() || undefined })}
            data-testid="onb-approve-confirm"
            className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-3 py-2 disabled:opacity-50"
          >
            {busy ? "Approving…" : "Approve stage"}
          </button>
        </div>
      </div>
    </div>
  );
}
